import React, {memo, useMemo, useState} from 'react'
import {NodeType} from '../../../../../Utils/Constant/FlowConstants'
import TriggerSidebar from '../FlowCanvas/components/TriggerSidebar'
import SMSSidebar from '../FlowCanvas/components/SMSSidebar'
import TimeDelaySidebar from '../FlowCanvas/components/TimeDelaySidebar'
import ConditionalSidebar from '../FlowCanvas/components/ConditionalSidebar'
import WaitForReplySidebar from '../FlowCanvas/components/WaitForReplySidebar'
import AddTagSidebar from '../FlowCanvas/components/AddTagSidebar'
import ConditionDeleteModal from '../FlowCanvas/components/ConditionDeleteModal'
import HowToVideosModal from './HowToVideosModal'
import {useStore, useReactFlow, type Node} from 'reactflow'
import {toast} from 'react-toastify'
import {useFlowNodeDelete} from '../FlowCanvas/hooks/useFlowNodeDelete'
import {getConditionDeleteOptions, BranchType} from '../FlowCanvas/hooks/useConditionNodeDelete'
import './SidebarRight.scss'
import {DataNode, IDelayUnit, MetaDataWithAddTag, MetaDataWithCondition} from '../FlowTypes'

const nodeInternalsSelector = (state: {nodeInternals: Map<string, Node>}) => state.nodeInternals

export enum NODE_FEATURE_NAME {
  TRIGGER = 'trigger-node',
  SMS = 'sms-node',
  TIME_DELAY = 'time-delay-node',
  CONDITION = 'condition-node',
  ADD_TAG = 'add-tag-node',
  DEFAULT = 'node',
}

const SidebarRight: React.FC<{
  id: string
  onClose?: () => void
  onAutoSaveFlow?: () => void
  replyWindowMinutes: number
  onReplyWindowMinutesChange: (value: number) => void
}> = ({id, onClose, onAutoSaveFlow, replyWindowMinutes, onReplyWindowMinutesChange}) => {
  const nodes = useStore(nodeInternalsSelector)
  const {setNodes, setEdges, getEdges} = useReactFlow()
  const selectedNode = nodes.get(id)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showHowToModal, setShowHowToModal] = useState(false)

  const {handleDelete: deleteNode, handleConditionDelete} = useFlowNodeDelete({
    nodes: Array.from(nodes.values()),
    edges: getEdges(),
    setNodes,
    setEdges,
    flowName: '',
    flowStatus: 'draft',
    pushToUndoStack: undefined,
    isUndoRedoOperation: undefined,
  })

  const deleteOptions = getConditionDeleteOptions(Array.from(nodes.values()), getEdges(), id)

  const getFeatureName = (nodeType: string): string => {
    switch (nodeType) {
      case NodeType.TRIGGER:
        return NODE_FEATURE_NAME.TRIGGER
      case NodeType.SMS:
        return NODE_FEATURE_NAME.SMS
      case NodeType.DELAY:
        return NODE_FEATURE_NAME.TIME_DELAY
      case NodeType.CONDITION:
        return NODE_FEATURE_NAME.CONDITION
      case NodeType.ADD_TAG:
        return NODE_FEATURE_NAME.ADD_TAG
      default:
        return NODE_FEATURE_NAME.DEFAULT
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    if (selectedNode) {
      // Check if the node is a condition node
      if (selectedNode.type === NodeType.CONDITION && deleteOptions.length > 0) {
        setShowDeleteModal(true)
      } else {
        await deleteNode(id, selectedNode, e)
        toast.success('Node deleted successfully')
        if (onClose) onClose()
      }
    }
  }

  const handleCancel = () => {
    if (onClose) onClose()
  }

  const handleSave = () => {
    toast.success('Changes saved successfully')
    setTimeout(() => {
      if (onClose) onClose()
    }, 500)
  }

  const handleModalConfirm = (branchType: BranchType) => {
    handleConditionDelete(id, branchType)
    toast.success('Condition branch deleted successfully')
    setShowDeleteModal(false)
    if (onClose) onClose()
  }

  const handleModalHide = () => {
    setShowDeleteModal(false)
  }

  const renderSidebar = useMemo(() => {
    if (!selectedNode) return null
    switch (selectedNode.type) {
      case NodeType.TRIGGER:
        return <TriggerSidebar node={selectedNode} />
      case NodeType.SMS:
        return <SMSSidebar node={selectedNode} />
      case NodeType.DELAY:
        return <TimeDelaySidebar node={selectedNode as Node<DataNode<IDelayUnit>, NodeType>} />
      case NodeType.CONDITION:
        return (
          <ConditionalSidebar
            node={selectedNode as Node<DataNode<MetaDataWithCondition>, NodeType>}
          />
        )
      case NodeType.WAIT_FOR_REPLY:
        return (
          <WaitForReplySidebar
            value={replyWindowMinutes}
            onChange={onReplyWindowMinutesChange}
          />
        )
      case NodeType.ADD_TAG:
        return (
          <AddTagSidebar
            node={selectedNode as Node<DataNode<MetaDataWithAddTag>, NodeType>}
          />
        )
      default:
        return null
    }
  }, [selectedNode, onAutoSaveFlow, replyWindowMinutes, onReplyWindowMinutesChange])

  return (
    <div className='preview-container d-flex flex-column h-100 position-relative'>
      {onClose && (
        <div
          className='position-absolute'
          style={{top: 0, right: -20, zIndex: 10, display: 'flex', gap: '8px'}}
        >
          {/* How To Videos Button */}
          <button
            className='border-0 bg-transparent'
            onClick={() => setShowHowToModal(true)}
            style={{
              fontSize: 24,
              cursor: 'pointer',
              padding: '4px',
            }}
            aria-label='How to videos'
            title='How to videos'
          >
            <i className='bi bi-play-circle text-primary' style={{fontSize: 18}}></i>
          </button>

          {/* Close Button */}
          <button
            className='sidebar-close-btn border-0 bg-transparent'
            onClick={onClose}
            style={{
              fontSize: 24,
              cursor: 'pointer',
              padding: '4px',
            }}
            aria-label='Close sidebar'
          >
            <i className='bi bi-x-lg text-muted' style={{fontSize: 16}}></i>
          </button>
        </div>
      )}

      <div className='flex-fill overflow-auto'>{renderSidebar}</div>

      <div className='p-3 mt-auto'>
        <div className='d-flex justify-content-between align-items-center gap-3'>
          <button onClick={handleDelete} className='btn btn-outline-danger btn-sm border'>
            Delete
          </button>

          <div className='d-flex gap-2'>
            <button
              onClick={handleCancel}
              className='btn btn-outline-secondary btn-sm border text-black'
            >
              Cancel
            </button>

            <button onClick={handleSave} className='btn btn-primary btn-sm border'>
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Condition Delete Modal */}
      {showDeleteModal && (
        <ConditionDeleteModal
          show={showDeleteModal}
          onHide={handleModalHide}
          onConfirm={handleModalConfirm}
          deleteOptions={deleteOptions as BranchType[]}
        />
      )}

      {/* How To Videos Modal */}
      <HowToVideosModal
        show={showHowToModal}
        onHide={() => setShowHowToModal(false)}
        featureName={selectedNode?.type ? getFeatureName(selectedNode.type) : 'Workflow'}
        handleBackToList={() => setShowHowToModal(false)}
      />
    </div>
  )
}

export default memo(SidebarRight)
